import React from "react";
import { Stack } from "@mui/material";
import Selection from "components/Input/Selection/Selection";
import { Controller, useWatch } from "react-hook-form";
import locations from "../../../../../locale/en/location.json";
import { CustomComponentCommonProps, rules } from "../utils";
import { useTranslation } from "react-i18next";

export default function LocationCascader({
  control,
  setValue,
  isEdit,
  isLoading,
}) {
  const formValues = control._formValues;
  const { t } = useTranslation();
  const PAGE_LOCALE_KEY =
    "page.merchants.merchantDetail.info.merchantInfo.fields";

  // Watch values with fallback to form state
  const area = useWatch({ control, name: "storeArea" }) || formValues.storeArea;
  const district =
    useWatch({ control, name: "storeDistrict" }) || formValues.storeDistrict;

  // Generate options with fallback to form values
  const districtOptions =
    area || formValues.storeArea
      ? Object.keys(locations[area || formValues.storeArea] || {}).map(
          (district) => ({
            key: district,
            value: district,
            title: district,
          }),
        )
      : [];

  const subDistrictOptions =
    (area && district) || (formValues.storeArea && formValues.storeDistrict)
      ? (locations[area || formValues.storeArea]?.[
          district || formValues.storeDistrict
        ] || []).map((sub) => ({
          key: sub,
          value: sub,
          title: sub,
        }))
      : [];

  return (
    <Stack direction="row" spacing={2}>
      {/* Area Selection */}
      <Controller
        name="storeArea"
        defaultValue=""
        control={control}
        rules={rules({ required: true })}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <Selection
            errorMessage={error?.message || ""}
            options={Object.keys(locations).map((area) => ({
              key: area,
              value: area,
              title: area,
            }))}
            divStyle={{ width: "100%", flexGrow: 1 }}
            onchangeFn={(e) => {
              onChange(e.target.value);
              setValue("storeDistrict", "");
              setValue("storeSubdistrict", "");
            }}
            {...CustomComponentCommonProps(
              t(`${PAGE_LOCALE_KEY}.storeArea`),
              value,
              "Selection",
              true,
              isEdit,
              isLoading,
            )}
            sxParams={{
              selectionError: !!error,
            }}
          />
        )}
      />

      {/* District Selection */}
      <Controller
        name="storeDistrict"
        defaultValue=""
        control={control}
        rules={rules({ required: true })}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <Selection
            errorMessage={error?.message || ""}
            options={districtOptions}
            divStyle={{ width: "100%", flexGrow: 1 }}
            onchangeFn={(e) => {
              onChange(e.target.value);
              setValue("storeSubdistrict", "");
            }}
            {...CustomComponentCommonProps(
              t(`${PAGE_LOCALE_KEY}.storeDistrict`),
              value,
              "Selection",
              true,
              isEdit && area,
              isLoading,
            )}
            sxParams={{
              selectionError: !!error,
            }}
          />
        )}
      />

      {/* Sub-district Selection */}
      <Controller
        name="storeSubdistrict"
        defaultValue=""
        control={control}
        rules={rules({ required: true })}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <Selection
            errorMessage={error?.message || ""}
            options={subDistrictOptions}
            divStyle={{ width: "100%", flexGrow: 1 }}
            onchangeFn={(e) => {
              onChange(e.target.value);
            }}
            {...CustomComponentCommonProps(
              t(`${PAGE_LOCALE_KEY}.storeSubdistrict`),
              value,
              "Selection",
              true,
              isEdit && district,
              isLoading,
            )}
            sxParams={{
              selectionError: !!error,
            }}
          />
        )}
      />
    </Stack>
  );
}
